import { getScrollTop } from '../getScrollTop';

interface ScrollOptions {
  el?: HTMLElement | Window;
  top?: number;
  left?: number;
  behavior?: ScrollBehavior | 'instant';
  timeout?: number;
  isSkipSmoothForLongDistance?: boolean;
}

const LONG_DISTANCE = 5000;

const scroll = ({ el = window, top, left, behavior }: ScrollOptions) => {
  try {
    el.scrollTo({ top, left, behavior });
  } catch {
    el.scrollTo(Number(left), Number(top));
  }
};

// prevent delayed scroll if we trigger another scroll on the same el before it fires. Example - page auto scroll to top hindering any custom scroll to element logic on page load.
const timeoutIds: Map<HTMLElement | Window, NodeJS.Timeout> = new Map();

const clearScrollTimeout = (el?: HTMLElement | Window) => {
  const id = el && timeoutIds.get(el);

  if (el && id != null) {
    clearTimeout(id);
    timeoutIds.delete(el);
  }
};

const setScrollTimeout = <T extends () => void>(
  fn: T,
  el: HTMLElement | Window,
  timeout: number
) => {
  const timeoutId = setTimeout(() => {
    fn();
    timeoutIds.delete(el);
  }, timeout);

  timeoutIds.set(el, timeoutId);
};

export const scrollTo = (
  options: Omit<ScrollOptions, 'el'> & { el?: ScrollOptions['el'] | null } = {}
) => {
  const {
    el,
    top = 0,
    left = 0,
    behavior = 'auto',
    timeout = 0,
    isSkipSmoothForLongDistance = false
  } = options;
  if (el === null) {
    return; // in case when we pass a ref to the element
  }

  const currentScrollTop = getScrollTop(el as HTMLElement);
  const scrollDistance = top - currentScrollTop;

  clearScrollTimeout(el);

  if (behavior === 'instant') {
    scroll({ el, top, left, behavior });
    return;
  }

  if (
    Math.abs(scrollDistance) > LONG_DISTANCE &&
    !isSkipSmoothForLongDistance
  ) {
    setScrollTimeout(
      () => {
        scroll({
          el,
          top: top > currentScrollTop ? top - 1500 : top + 1500,
          left,
          behavior: 'auto'
        });

        scroll({
          el,
          top,
          left,
          behavior: 'smooth'
        });
      },
      el as HTMLElement,
      timeout
    );
  } else {
    setScrollTimeout(
      () => scroll({ el, top, left, behavior }),
      el as HTMLElement,
      timeout
    );
  }
};
